import { FC, MouseEvent, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Collapse, Typography, alpha, styled } from '@mui/material';
import { PolarisIcon } from '@/exports/columnCommonComponents';
import CalendarButtonInput from './CalendarButtonInput';
import Datepicker from '@/components/_common/Datepicker/Datepicker';
import FocusImage from '@/components/Focus/common/FocusImage';
import IncrementalLaunchDetailsHeader from './IncrementalLaunchDetailsHeader';
import IncrementalLaunchDetailsRow from './IncrementalLaunchDetailsRow';
import IncrementalLaunchWorkspace, { IncrementalLaunchWorkspaceDraft } from './AddIncrementalLaunch/IncrementalLaunchWorkspace';
import PlatformsList from '@/components/_common/Table/Calendar/CalendarSharedComponents/PlatformsList';
import RoundIconButton from '@/components/Focus/common/RoundIconButton';
import ScheduleAvailChipButton from '../../ScheduleAvailChipButton';
import { FocusTheme } from '@/components/Focus/configs/theme';
import { LicenseChip } from '@/components/_common/Table/Calendar/CalendarComponents/common';
import { ThumbnailPlaceholder } from '@/components/Focus/Avails/columns';
import { TitleEntity } from '@/redux/slices/slice_types';
import { TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';
import { createAvailChipItems, formatDateTimePartsLocal, getOccurrenceIso } from '@/components/Focus/utils/newScheduleAvails';
import { createLaunch } from '@/components/Focus/utils/incrementalLaunchWorkspace';
import { makeSelectVersionHierarchyEntities } from '@/redux/selectors/orderPlacementSelectors';
import { rootElementId } from '@/constants/constants';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  addLaunchAndValidateRights,
  deleteLaunchAndValidateRights,
  initializeLaunchList,
  Launch,
  setScheduleData,
  updateLaunchAndValidateRights
} from '@/redux/slices/focusNewScheduleSlice';
import { selectFocusNewScheduleSlice, selectLastIncrementalLaunch, selectLaunchList } from '@/redux/selectors/focusNewScheduleSelectors';
import Tooltip from '@/components/_common/Tooltip';
import { getPreviousIncrementChanges } from '@/components/Focus/utils/incrementalLaunchList';
import { selectAllLanguagesMap } from '@/redux/selectors/constantsSelectors';

type Option = { label: string; value: string };

export type ScheduleRow = {
  versionId: number;
  occurrenceStartDate: string;
  selectedTerritories: string[];
  selectedDubs: string[];
  selectedSubs: string[];
  selectedVersions: (TitleEntity | TitleEntityV2)['id'][];
};

export type ReviewScheduleAccordionHeaderProps = {
  version: any;
  frequency: string;
  startDate: string;
  licence: string;
  platforms: string[];
  row?: ScheduleRow;
  territoryItems: Option[];
  dubItems: Option[];
  subItems: Option[];
  versionItems: Option[];
  updateGroupRoot: (keyVersionId: number, patch: Partial<Omit<ScheduleRow, 'versionId'>>) => void;
  onIncrementalLaunchWorkspaceOpenChange?: (isOpen: boolean) => void;
};

const ReviewScheduleAccordionHeader: FC<ReviewScheduleAccordionHeaderProps> = ({
  version,
  frequency,
  startDate,
  licence,
  platforms,
  row,
  territoryItems,
  dubItems,
  subItems,
  versionItems,
  updateGroupRoot,
  onIncrementalLaunchWorkspaceOpenChange
}) => {
  const dispatch = useAppDispatch();

  const [editingLaunchId, setEditingLaunchId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [showIncrementalLaunchWorkspace, setShowIncrementalLaunchWorkspace] = useState(false);
  const scheduleData = useAppSelector(selectFocusNewScheduleSlice);

  const keyVersionId = row?.versionId ?? version.id;
  const rowStartDateIso = row?.occurrenceStartDate ?? getOccurrenceIso(startDate, frequency, 0);
  const { date, time } = formatDateTimePartsLocal(rowStartDateIso);

  const launchList = useAppSelector(selectLaunchList);
  const lastIncrementalLaunch = useAppSelector(selectLastIncrementalLaunch);
  const allLanguagesMap = useAppSelector(selectAllLanguagesMap);

  const { selectedDubs, selectedSubs, selectedTerritories } = getPreviousIncrementChanges({
    launchList,
    originalSelectedDubs: row?.selectedDubs ?? [],
    originalSelectedSubs: row?.selectedSubs ?? [],
    originalSelectedTerritories: row?.selectedTerritories ?? [],
    originalSelectedVersions: row?.selectedVersions ?? []
  });

  const selectedDubItems = createAvailChipItems({
    values: selectedDubs,
    type: 'audio',
    allLanguagesMap
  });

  const selectedSubItems = createAvailChipItems({
    values: selectedSubs,
    type: 'timedtext',
    allLanguagesMap
  });

  const selectedTerritoryItems = createAvailChipItems({
    values: selectedTerritories,
    type: 'territories',
    allLanguagesMap
  });

  const selectVersionHierarchyEntities = useMemo(makeSelectVersionHierarchyEntities, []);
  const { seriesEntity, episodeCount } = useAppSelector((state) => selectVersionHierarchyEntities(state, keyVersionId));

  const toEditIncrementalLaunchDraft = useMemo<IncrementalLaunchWorkspaceDraft | undefined>(() => {
    const selectedLaunchItem = launchList.find((l) => l.id === editingLaunchId);
    if (!editingLaunchId || !selectedLaunchItem) {
      if (!lastIncrementalLaunch) {
        return undefined;
      }

      return {
        incrementalLaunchTimingValue: lastIncrementalLaunch.incrementalLaunchTimingValue,
        incrementalLaunchRightsDraftByTab: lastIncrementalLaunch.incrementalLaunchRightsDraftByTab,
        incrementalLaunchVersions: lastIncrementalLaunch.incrementalLaunchVersions
      };
    }

    return {
      incrementalLaunchTimingValue: selectedLaunchItem.incrementalLaunchTimingValue,
      incrementalLaunchRightsDraftByTab: selectedLaunchItem.incrementalLaunchRightsDraftByTab,
      incrementalLaunchVersions: selectedLaunchItem.incrementalLaunchVersions
    };
  }, [editingLaunchId, launchList, lastIncrementalLaunch]);

  useEffect(() => {
    dispatch(initializeLaunchList());
  }, [dispatch]);

  useEffect(() => {
    onIncrementalLaunchWorkspaceOpenChange?.(showIncrementalLaunchWorkspace);
  }, [showIncrementalLaunchWorkspace, onIncrementalLaunchWorkspaceOpenChange]);

  const handleAccordionChange = (_event: SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded);
  };

  const handleAddIncrementalLaunch = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (showIncrementalLaunchWorkspace) {
      return;
    }

    setEditingLaunchId(null);
    setExpanded(true);
    setIsEditing(false);
    setShowIncrementalLaunchWorkspace(true);
  };

  const handleCloseIncrementalLaunchWorkspace = () => {
    setEditingLaunchId(null);
    setIsEditing(false);
    setShowIncrementalLaunchWorkspace(false);
  };

  const handleDeleteLaunch = (launchId: string) => {
    dispatch(
      deleteLaunchAndValidateRights({
        launchId
      })
    );
  };

  const handleDuplicateLastIncrementalLaunch = () => {
    if (showIncrementalLaunchWorkspace || !lastIncrementalLaunch) {
      return;
    }

    setEditingLaunchId(lastIncrementalLaunch.id);
    setExpanded(true);
    setIsEditing(true);
    setShowIncrementalLaunchWorkspace(true);
  };

  const handleEditIncrementalLaunch = (id: Launch['id']) => {
    setEditingLaunchId(id);
    setExpanded(true);
    setIsEditing(true);
    setShowIncrementalLaunchWorkspace(true);
  };

  const handleEditFirstTimeLaunch = () => {
    if (showIncrementalLaunchWorkspace) {
      return;
    }

    setEditingLaunchId(null);
    setExpanded(true);
    setIsEditing(true);
    setShowIncrementalLaunchWorkspace(true);
  };

  const handleSaveIncrementalLaunchWorkspace = (draft: IncrementalLaunchWorkspaceDraft) => {
    if (editingLaunchId) {
      dispatch(
        updateLaunchAndValidateRights({
          launchId: editingLaunchId,
          patch: {
            incrementalLaunchTimingValue: draft.incrementalLaunchTimingValue,
            incrementalLaunchRightsDraftByTab: draft.incrementalLaunchRightsDraftByTab,
            incrementalLaunchVersions: draft.incrementalLaunchVersions
          }
        })
      );
    } else {
      const terValus = draft.incrementalLaunchRightsDraftByTab.territories.remove;
      const audio = draft.incrementalLaunchRightsDraftByTab.audio.remove;
      const timedText = draft.incrementalLaunchRightsDraftByTab.timedtext.remove;
      dispatch(
        setScheduleData({
          selectedTerritories: scheduleData.selectedTerritories.filter((t) => !terValus.includes(t)),
          selectedDubs: scheduleData.selectedDubs.filter((a) => !audio.includes(a)),
          selectedSubs: scheduleData.selectedSubs.filter((s) => !timedText.includes(s))
        })
      );

      dispatch(
        addLaunchAndValidateRights({
          launch: {
            ...createLaunch('incremental'),
            incrementalLaunchTimingValue: draft.incrementalLaunchTimingValue,
            incrementalLaunchRightsDraftByTab: {
              territories: { add: terValus, remove: [] },
              audio: { add: audio, remove: [] },
              timedtext: { add: timedText, remove: [] }
            },
            incrementalLaunchVersions: draft.incrementalLaunchVersions
          }
        })
      );
    }

    setEditingLaunchId(null);
    setIsEditing(false);
    setShowIncrementalLaunchWorkspace(false);
  };

  return (
    <StyledAccordion expanded={expanded} onChange={handleAccordionChange} disableGutters>
      <StyledAccordionSummary className="reviewScheduleSummary" expandIcon={<RoundIconButton icon="down" />}>
        <StyledRow>
          <StyledLeftRow>
            <ThumbnailPlaceholder>
              <FocusImage entity={{ title: seriesEntity }} />
            </ThumbnailPlaceholder>
            <StyledTitleMeta>
              {!!seriesEntity?.name && <StyledSeriesName variant="h6">{seriesEntity?.name}</StyledSeriesName>}
              {!!episodeCount && (
                <StyledMetaRow>
                  {version.lineage_meta.parent_entity_type === 'movie' ? (
                    <>Movie</>
                  ) : (
                    <>
                      <span>Season</span>
                      {!!episodeCount && <span>•</span>}
                      {!!episodeCount && (
                        <span>
                          {episodeCount} Episode{episodeCount !== 1 ? 's' : ''}
                        </span>
                      )}
                    </>
                  )}
                </StyledMetaRow>
              )}
            </StyledTitleMeta>
            <LicenseChip label={licence.toUpperCase()} variant="outlined" />
            <PlatformsList platforms={platforms || []} limit={4} />
          </StyledLeftRow>
          <StyledLeftBorder>
            <StyledSectionLabel>First Release Date</StyledSectionLabel>
            <Datepicker
              bare
              value={rowStartDateIso}
              showTimeSelect
              timeIntervals={15}
              timeCaption="Time"
              showIcon={false}
              portalId={rootElementId}
              popperPlacement="bottom-start"
              popperProps={{ strategy: 'fixed' }}
              onChange={(dateValue) => {
                if (!dateValue) return;
                updateGroupRoot(keyVersionId, { occurrenceStartDate: dateValue.toISOString() });
              }}
              customInput={<CalendarButtonInput date={date} time={time} />}
            />
          </StyledLeftBorder>
          <StyledLeftBorder>
            <StyledSectionLabel>Schedule Summary</StyledSectionLabel>
            <StyledAvailChipRow>
              <ScheduleAvailChipButton
                icon={<PolarisIcon icon="map" color="white" />}
                label="Territories"
                caption={selectedTerritoryItems.length}
                items={selectedTerritoryItems}
              />
              <ScheduleAvailChipButton
                icon={<PolarisIcon icon="music" color="white" />}
                label="Audio"
                caption={selectedDubItems.length}
                items={selectedDubItems}
              />
              <ScheduleAvailChipButton
                icon={<PolarisIcon icon="subtitles" color="white" />}
                label="Timed Text"
                caption={selectedSubItems.length}
                items={selectedSubItems}
              />
            </StyledAvailChipRow>
          </StyledLeftBorder>
        </StyledRow>
        <Tooltip title="Add incremental launch" placement="top">
          <StyledIconContainer>
            <RoundIconButton
              icon="add"
              aria-label="Add incremental launch"
              onClick={handleAddIncrementalLaunch}
              disabled={showIncrementalLaunchWorkspace}
            />
          </StyledIconContainer>
        </Tooltip>
      </StyledAccordionSummary>
      <StyledAccordionDetails>
        <IncrementalLaunchDetailsHeader />
        {launchList.map((launch) => (
          <IncrementalLaunchDetailsRow
            key={launch.id}
            launch={launch}
            releaseDate={date}
            releaseTime={time}
            territoryItems={territoryItems}
            dubItems={dubItems}
            subItems={subItems}
            versionItems={versionItems}
            onDelete={launch.type === 'first-time' || showIncrementalLaunchWorkspace ? undefined : () => handleDeleteLaunch(launch.id)}
            isEditing={editingLaunchId === launch.id}
            onEdit={
              showIncrementalLaunchWorkspace
                ? undefined
                : launch.type === 'first-time'
                  ? handleEditFirstTimeLaunch
                  : launch.id === lastIncrementalLaunch?.id
                    ? handleDuplicateLastIncrementalLaunch
                    : () => handleEditIncrementalLaunch(launch.id)
            }
          />
        ))}
        <Collapse in={showIncrementalLaunchWorkspace} timeout="auto" unmountOnExit>
          <IncrementalLaunchWorkspace
            isEditing={isEditing}
            initialDraft={editingLaunchId ? toEditIncrementalLaunchDraft : undefined}
            initialId={editingLaunchId ?? undefined}
            onCancel={handleCloseIncrementalLaunchWorkspace}
            onSave={handleSaveIncrementalLaunchWorkspace}
            initialReleaseDateIso={rowStartDateIso}
          />
        </Collapse>
      </StyledAccordionDetails>
    </StyledAccordion>
  );
};

export default ReviewScheduleAccordionHeader;

const StyledAccordion = styled(Accordion)`
  background-color: ${({ theme }) => theme.palette.background.default};
`;

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  '&.MuiAccordionSummary-root': {
    padding: theme.spacing(2),
    backgroundColor: (theme as FocusTheme).palette.neutral[70],
    borderBottom: `3px solid ${theme.palette.background.default}`,
    minHeight: '84px',
    borderRadius: `${theme.shape.borderRadius}px`,
    gap: theme.spacing(2)
  },
  '& .MuiAccordionSummary-content': {
    marginTop: 0,
    marginBottom: 0,
    order: 1,
    justifyContent: 'space-between'
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    alignItems: 'center',
    '& svg': { transform: 'rotate(-90deg)' },
    '&.Mui-expanded': {
      transform: 'none',
      '& svg': { transform: 'rotate(0deg)' }
    }
  }
}));

const StyledAccordionDetails = styled(AccordionDetails)`
  &.MuiAccordionDetails-root {
    padding: 0;
  }
`;

const StyledRow = styled('div')`
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const StyledLeftRow = styled('div')`
  height: 100%;
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const StyledTitleMeta = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 300px;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const StyledSeriesName = styled(Typography)`
  font-weight: ${({ theme }) => theme.typography.fontWeightNormal};
`;

const StyledMetaRow = styled(Typography)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  min-width: 0;
  font-size: 14px;
  line-height: 1.2;
  color: ${({ theme }) => (theme as FocusTheme).palette.neutral[20]};
  text-wrap: nowrap;
`;

const StyledIconContainer = styled('div')`
  align-items: center;
  display: flex;
`;

export const StyledLeftBorder = styled('div')<{
  accent?: string;
}>(({ theme, accent }) => ({
  position: 'relative',
  display: 'inline-flex',
  flexDirection: 'column',
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 6,
    backgroundColor: accent ?? alpha(theme.palette.common.black, 0.2),
    borderRadius: 999
  }
}));

const StyledAvailChipRow = styled('div')`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1)};
  align-items: center;
  margin-right: 12px;
  margin-top: 8px;
`;

const StyledSectionLabel = styled(Typography)`
  font-size: 14px;
  line-height: 1.2;
  color: ${({ theme }) => (theme as FocusTheme).palette.neutral[20]};
`;
